import { __decorate } from "tslib";
import { html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { live } from 'lit/directives/live.js';
import { FormSubmitController } from '../../utilities/controllers/forms';
import { emitEvent } from '../../utilities/misc/events';
import { stylesBase } from './styles';
/**
 * @since 1.0.0
 * @status stable
 *
 * @tagname kemet-input
 * @summary An enhanced input element.
 *
 * @prop {string} slug - Used for the id of the input. Should match the slug used in a control if applicable.
 * @prop {string} name - The name of the input
 * @prop {string} placeholder - The placeholder attribute
 * @prop {string} minlength - The minlength attribute
 * @prop {string} maxlength - The maxlength attribute
 * @prop {string} min - The min attribute
 * @prop {string} max - The max attribute
 * @prop {string} step - The step attribute
 * @prop {string} autocomplete - The autocomplete attribute
 * @prop {string} pattern - The pattern attribute
 * @prop {string} inputmode - The input mode attribute
 * @prop {boolean} autofocus - The autofocus attribute
 * @prop {boolean} disabled - The disable attribute
 * @prop {boolean} readonly - The readonly attribute
 * @prop {boolean} required - The required attribute
 * @prop {string} type - The type of input
 * @prop {string} value - The input's value
 * @prop {boolean} invalid - States whether the input is invalid
 * @prop {string} status - The status of the input
 * @prop {boolean} validateOnBlur - Activates validation on blur
 * @prop {string} ariaAutoComplete - Aria Autocomplete
 * @prop {string} ariaControls - Aria Controls
 * @prop {string} ariaActiveDescendant - Aria Active Descendant
 * @prop {boolean} rounded - Displays rounded corners
 * @prop {boolean} filled - Displays a filled input box
 * @prop {string} iconRight - Custom Icon to the right of the input
 * @prop {string} iconLeft - Custom Icon to the left of the input
 * @prop {number} iconSize - Size of the icons
 * @prop {object} validity - The HTML5 validity object.
 * @prop {boolean} isPasswordVisible - Manages password visibility
 * @prop {string} inputType - Input Type of keypress handled through handleInput(e)
 * @prop {boolean} focused - Determines if the input is focused
 *
 * @csspart input
 *
 * @cssproperty --kemet-input-height - The height of the input.
 * @cssproperty --kemet-input-padding - The padding on the input.
 * @cssproperty --kemet-input-border - The border of the input.
 * @cssproperty --kemet-input-border-color-error -  The border of the error state.
 * @cssproperty --kemet-input-border-color-success - The border of the success state.
 * @cssproperty --kemet-input-border-color-warning - The border of the warning state.
 * @cssproperty --kemet-input-icon-left-padding - The icon-left padding.
 * @cssproperty --kemet-input-icon-right-padding - The icon-right padding.
 * @cssproperty --kemet-input-border-radius-rounded - The border radius on rounded.
 * @cssproperty --kemet-input-border-filled - The border on filled.
 * @cssproperty --kemet-input-color-filled - The color on filled.
 * @cssproperty --kemet-input-background-color-filled - The background-color on filled.
 * @cssproperty --kemet-input-background-color-error - The error state background color.
 * @cssproperty --kemet-input-background-color-success - The success state background color.
 * @cssproperty --kemet-input-background-color-warning - The warning state background color.
 *
 * @event kemet-input-focused - Fires when the input receives and loses focus
 * @event kemet-input-status Fires when there's a change in status. This event includes an object that reports: 1) the status. 2) HTML5 validity object. 3) the component element.
 * Use the validity object to support custom validation messages.
 * @event kemet-input-input - Fires when the input receives input
 *
 */
let KemetInput = class KemetInput extends LitElement {
    constructor() {
        super();
        this.name = 'input';
        this.type = 'text';
        this.value = '';
        this.status = 'standard';
        this.validateOnBlur = false;
        this.iconSize = 20;
        this.isPasswordVisible = false;
        this.focused = false;
        /** @internal */
        this.formSubmitController = new FormSubmitController(this);
    }
    firstUpdated() {
        // elements
        this.input = this.shadowRoot.querySelector('input');
        this.field = this.closest('kemet-field');
        this.form = this.closest('form');
        this.slug = this.field ? this.field.slug : 'input';
        if (this.field) {
            this.field.addEventListener('fmc-password-status', (event) => this.handleStatus(event));
        }
    }
    render() {
        return html `
      <div>
        ${this.makeIconLeft()}
        <input
          part="input"
          id=${this.slug}
          name=${this.name}
          placeholder=${ifDefined(this.placeholder)}
          minlength=${ifDefined(this.minlength)}
          maxlength=${ifDefined(this.maxlength)}
          min=${ifDefined(this.min)}
          max=${ifDefined(this.max)}
          step=${ifDefined(this.step)}
          autocomplete=${ifDefined(this.autocomplete)}
          aria-autocomplete=${ifDefined(this.ariaAutoComplete)}
          aria-controls=${ifDefined(this.ariaControls)}
          aria-activedescendant=${ifDefined(this.ariaActiveDescendant)}
          pattern=${ifDefined(this.pattern)}
          inputmode=${ifDefined(this.inputmode)}
          aria-invalid=${this.invalid ? 'true' : 'false'}
          ?autofocus=${this.autofocus}
          ?disabled=${this.disabled}
          ?readonly=${this.readonly}
          ?required=${this.required}
          .type="${this.type === 'password' && this.isPasswordVisible ? 'text' : this.type}"
          .value=${live(this.value)}
          @change=${this.handleChange}
          @input=${this.handleInput}
          @invalid=${this.handleInvalid}
          @focus=${this.handleFocus}
          @blur=${this.handleBlur}
        />
        ${this.makeIconRight()} ${this.makeIconClear()} ${this.makeVisibilityToggle()}
      </div>
    `;
    }
    makeIconRight() {
        if (this.iconRight) {
            return html `
        <kemet-icon class="right" icon="${this.iconRight}" size="${this.iconSize ? this.iconSize : 20}"></kemet-icon>
      `;
        }
        return null;
    }
    makeIconLeft() {
        if (this.iconLeft) {
            return html `
        <kemet-icon class="left" icon="${this.iconLeft}" size="${this.iconSize ? this.iconSize : 16}"></kemet-icon>
      `;
        }
        return null;
    }
    makeIconClear() {
        if (this.type === 'search' && this.value !== '') {
            return html `
        <kemet-icon class="search right" icon="x-lg" size="${this.iconSize}" @click=${() => this.handleClear()}></kemet-icon>
      `;
        }
        return null;
    }
    makeVisibilityToggle() {
        if (this.type === 'password') {
            return html `<kemet-icon
        class="eye right"
        icon="${this.setPasswordIcon()}"
        @click=${() => this.togglePasswordVisibility()}
      ></kemet-icon>`;
        }
        return null;
    }
    setPasswordIcon() {
        if (this.isPasswordVisible) {
            return 'eye';
        }
        return 'eye-slash';
    }
    /**
     * Handles when the input receives focus
     * @private
     */
    handleFocus() {
        this.focused = true;
        emitEvent(this, 'kemet-input-focused', true);
    }
    /**
     * Handles when the input loses focus
     * @private
     */
    handleBlur() {
        var _a, _b;
        if (this.validateOnBlur && this.form && !this.form.noValidate) {
            (_a = this.input) === null || _a === void 0 ? void 0 : _a.checkValidity();
            this.validity = (_b = this.input) === null || _b === void 0 ? void 0 : _b.validity;
        }
        this.focused = false;
        emitEvent(this, 'kemet-input-focused', false);
    }
    /**
     * Handles when the input changes value
     * @private
     */
    handleChange() {
        this.value = this.input.value;
        if (this.input.checkValidity() && this.checkLimitValidity() && this.status !== 'success') {
            this.invalid = false;
            this.status = 'standard';
            this.validity = this.input.validity;
            emitEvent(this, 'kemet-input-status', {
                status: 'standard',
                validity: this.input.validity,
                element: this,
            });
        }
    }
    /**
     * Handles when the input receives input
     * @private
     */
    handleInput() {
        this.value = this.input.value;
        emitEvent(this, 'kemet-input-input', this.value);
    }
    /**
     * Handles when the input has an error
     * @private
     */
    handleInvalid() {
        var _a, _b;
        this.validity = (_a = this.input) === null || _a === void 0 ? void 0 : _a.validity;
        if (this.validateOnBlur) {
            this.invalid = true;
            this.status = 'error';
            emitEvent(this, 'kemet-input-status', {
                status: 'error',
                validity: (_b = this.input) === null || _b === void 0 ? void 0 : _b.validity,
                element: this,
            });
        }
    }
    handleStatus(event) {
        this.status = event.detail.status;
    }
    /**
     * Toggles password visibility
     * @private
     */
    togglePasswordVisibility() {
        this.isPasswordVisible = !this.isPasswordVisible;
    }
    /**
     * Handles the clear button for search
     * @private
     */
    handleClear() {
        this.value = '';
        emitEvent(this, 'kemet-input-input', this.value);
    }
    /**
     * Checks the validity of the character limit for the count component
     * @public
     * @returns {boolean}
     */
    checkLimitValidity() {
        if (this.field) {
            const count = this.field.querySelector('kemet-count');
            if (count) {
                return this.value.length < count.limit;
            }
        }
        return true;
    }
    /**
     * Checks the validity of the standard input
     * @public
     * @returns {boolean}
     */
    checkValidity() {
        var _a;
        return (_a = this.input) === null || _a === void 0 ? void 0 : _a.checkValidity();
    }
    /**
     * Focuses the standard input
     * @public
     */
    focus() {
        var _a;
        (_a = this.input) === null || _a === void 0 ? void 0 : _a.focus();
    }
};
KemetInput.styles = [stylesBase];
__decorate([
    property({ type: String })
], KemetInput.prototype, "slug", void 0);
__decorate([
    property({ type: String })
], KemetInput.prototype, "name", void 0);
__decorate([
    property({ type: String })
], KemetInput.prototype, "placeholder", void 0);
__decorate([
    property({ type: Number })
], KemetInput.prototype, "minlength", void 0);
__decorate([
    property({ type: Number })
], KemetInput.prototype, "maxlength", void 0);
__decorate([
    property({ type: String })
], KemetInput.prototype, "min", void 0);
__decorate([
    property({ type: String })
], KemetInput.prototype, "max", void 0);
__decorate([
    property({ type: Number })
], KemetInput.prototype, "step", void 0);
__decorate([
    property({ type: String })
], KemetInput.prototype, "autocomplete", void 0);
__decorate([
    property({ type: String })
], KemetInput.prototype, "pattern", void 0);
__decorate([
    property({ type: String })
], KemetInput.prototype, "inputmode", void 0);
__decorate([
    property({ type: Boolean })
], KemetInput.prototype, "autofocus", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], KemetInput.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean })
], KemetInput.prototype, "readonly", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], KemetInput.prototype, "required", void 0);
__decorate([
    property({ type: String })
], KemetInput.prototype, "type", void 0);
__decorate([
    property({ type: String, reflect: true })
], KemetInput.prototype, "value", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], KemetInput.prototype, "invalid", void 0);
__decorate([
    property({ type: String, reflect: true })
], KemetInput.prototype, "status", void 0);
__decorate([
    property({ type: Boolean, attribute: 'validate-on-blur' })
], KemetInput.prototype, "validateOnBlur", void 0);
__decorate([
    property({ type: String, attribute: 'aria-autocomplete' })
], KemetInput.prototype, "ariaAutoComplete", void 0);
__decorate([
    property({ type: String, attribute: 'aria-controls' })
], KemetInput.prototype, "ariaControls", void 0);
__decorate([
    property({ type: String, attribute: 'aria-activedescendant' })
], KemetInput.prototype, "ariaActiveDescendant", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], KemetInput.prototype, "rounded", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], KemetInput.prototype, "filled", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'icon-right' })
], KemetInput.prototype, "iconRight", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'icon-left' })
], KemetInput.prototype, "iconLeft", void 0);
__decorate([
    property({ type: Number })
], KemetInput.prototype, "iconSize", void 0);
__decorate([
    property({ type: Object })
], KemetInput.prototype, "validity", void 0);
__decorate([
    property({ type: Boolean })
], KemetInput.prototype, "isPasswordVisible", void 0);
__decorate([
    property({ type: String })
], KemetInput.prototype, "inputType", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], KemetInput.prototype, "focused", void 0);
__decorate([
    state()
], KemetInput.prototype, "field", void 0);
__decorate([
    state()
], KemetInput.prototype, "form", void 0);
__decorate([
    state()
], KemetInput.prototype, "input", void 0);
KemetInput = __decorate([
    customElement('kemet-input')
], KemetInput);
export default KemetInput;
//# sourceMappingURL=kemet-input.js.map